import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from '@/components/utils/translations';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { ChevronDown, Loader2, Upload, FileText, AlertCircle, RefreshCw, CheckCircle2, AlertTriangle, Trash2 } from 'lucide-react';
import { Progress } from '@/components/ui/progress';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  fetchFitLatest,
  refreshLightFit,
  triggerDeepFit,
  pollFitJob,
  uploadTenderDocs,
  fetchTenderDocs,
  deleteTenderDoc,
  fetchProductProfiles,
} from '@/lib/api/fit';
import { toast } from 'sonner';
import { logProductProfile } from '@/lib/productProfileDebug';

const FIT_LABEL_COLORS = {
  'Strong Fit': 'bg-green-100 text-green-800 border-green-200',
  'Moderate Fit': 'bg-amber-100 text-amber-800 border-amber-200',
  'Weak Fit': 'bg-orange-100 text-orange-800 border-orange-200',
  'No Fit': 'bg-red-100 text-red-800 border-red-200',
  'Insufficient Info': 'bg-gray-100 text-gray-600 border-gray-200',
  'Low Evidence': 'bg-slate-100 text-slate-700 border-slate-200',
};

// Fit labels arrive as English enum values (also used for colours / sorting); translate only for display.
const FIT_LABEL_KEYS = {
  'Strong Fit': 'strongFit',
  'Moderate Fit': 'moderateFit',
  'Weak Fit': 'procWeakFit',
  'No Fit': 'procNoFit',
  'Insufficient Info': 'insufficientInfo',
  'Low Evidence': 'procLowEvidence',
};

const MATCH_STATUS_COLORS = {
  meets: 'bg-green-100 text-green-800',
  partial: 'bg-amber-100 text-amber-800',
  no: 'bg-red-100 text-red-800',
  unknown: 'bg-gray-100 text-gray-600',
};

const REQ_TYPE_LABELS = { mandatory: 'Mandatory', scored: 'Scored', info: 'Info' };

const POSITIVE_REASON_TYPES = new Set(['strong_hit', 'positive_signal', 'incumbent_boost']);
const CONCERN_REASON_TYPES = new Set(['negative_hit', 'concern', 'incumbent_penalty']);

function FitReasonRow({ reason }) {
  const { type, text, excerpt } = reason;
  if (POSITIVE_REASON_TYPES.has(type)) {
    return (
      <div className="flex gap-2 text-green-700">
        <CheckCircle2 className="w-3.5 h-3.5 shrink-0 mt-0.5" />
        <div>
          <span>{text}</span>
          {excerpt && <p className="text-gray-500 mt-0.5 italic">{excerpt}</p>}
        </div>
      </div>
    );
  }
  if (CONCERN_REASON_TYPES.has(type)) {
    return (
      <div className="flex gap-2 text-amber-700">
        <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
        <div>
          <span>{text}</span>
          {excerpt && <p className="text-gray-500 mt-0.5 italic">{excerpt}</p>}
        </div>
      </div>
    );
  }
  return (
    <div className="text-gray-600">
      <span className="font-medium">{type}:</span> {text}
      {excerpt && <p className="text-gray-500 mt-0.5 italic">{excerpt}</p>}
    </div>
  );
}

// Profile names are stored as "<Vendor> — <product (detail)>"; the vendor is the tenant itself.
function productLabel(name) {
  if (!name) return 'Product';
  const parts = String(name).split(' — ');
  return parts.length > 1 ? parts.slice(1).join(' — ') : name;
}

function FitSummaryBlock({ assessment, title }) {
  const { t } = useTranslation();
  if (!assessment) return null;
  const { score, label, recommendation, confidence, outcome } = assessment;
  const colors = FIT_LABEL_COLORS[label] || 'bg-gray-100 text-gray-600 border-gray-200';
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2 flex-wrap">
        {score != null && (
          <div className="flex items-center gap-2">
            <Progress value={score} className="w-20 h-2" />
            <span className="text-sm font-medium">{score}</span>
          </div>
        )}
        <Badge variant="outline" className={`border ${colors}`}>{FIT_LABEL_KEYS[label] ? t(FIT_LABEL_KEYS[label]) : label}</Badge>
        <Badge variant="outline" className="text-xs">Confidence: {confidence}</Badge>
      </div>
      {recommendation && <p className="text-sm text-gray-600">{recommendation}</p>}
      {outcome === 'insufficient_information' && (
        <p className="text-xs text-amber-700 flex items-center gap-1">
          <AlertCircle className="w-3.5 h-3.5" />
          Source page could not be analyzed — run Deep Fit with tender documents
        </p>
      )}
    </div>
  );
}

export default function ProductFitPanel({ deal, tenantId, onFitLatestSynced }) {
  const { t } = useTranslation();
  const [fitData, setFitData] = useState(null);
  const [profiles, setProfiles] = useState([]);
  const [selectedProfileId, setSelectedProfileId] = useState('');
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deepRunning, setDeepRunning] = useState(false);
  const [deepStatus, setDeepStatus] = useState('');
  const [uploading, setUploading] = useState(false);
  const [docToDelete, setDocToDelete] = useState(null);
  const [deletingDocId, setDeletingDocId] = useState(null);

  const dealId = deal?.deal_id ?? deal?.id ?? deal?.tender_id;

  const loadFit = useCallback(async () => {
    if (!tenantId || !dealId) return;
    try {
      const data = await fetchFitLatest(tenantId, dealId);
      setFitData(data);
      const reasons = data?.light?.reasons || [];
      const incumbentReasons = reasons.filter((r) =>
        r?.type === 'incumbent_boost' || r?.type === 'incumbent_penalty',
      );
      if (incumbentReasons.length > 0) {
        logProductProfile('fit.light_reasons.incumbent', {
          tenantId,
          dealId,
          count: incumbentReasons.length,
          types: incumbentReasons.map((r) => r.type),
          texts: incumbentReasons.map((r) => r.text),
        });
      }
      logProductProfile('fit.light_reasons.summary', {
        tenantId,
        dealId,
        totalReasons: reasons.length,
        reasonTypes: [...new Set(reasons.map((r) => r?.type).filter(Boolean))],
        score: data?.light?.score,
        label: data?.light?.label,
      });
    } catch (err) {
      console.warn('[ProductProfile] fit.fetch.error', err);
      setFitData(null);
    } finally {
      setLoading(false);
    }
  }, [tenantId, dealId]);

  const loadProfiles = useCallback(async () => {
    if (!tenantId) return;
    try {
      const list = await fetchProductProfiles(tenantId);
      setProfiles(list);
      // Deep Fit uses one profile; default to first active when none selected (multiple can be active).
      const firstActive = list.find((p) => p.is_active);
      if (firstActive && !selectedProfileId) setSelectedProfileId(firstActive.id);
    } catch (err) {
      console.warn('Profiles fetch failed:', err);
    }
  }, [tenantId]);

  const loadDocs = useCallback(async () => {
    if (!tenantId || !dealId) return;
    try {
      const list = await fetchTenderDocs(tenantId, dealId);
      setDocs(list);
    } catch (err) {
      console.warn('Docs fetch failed:', err);
    }
  }, [tenantId, dealId]);

  useEffect(() => {
    if (tenantId && dealId) {
      setLoading(true);
      loadFit();
      loadProfiles();
      loadDocs();
    }
  }, [tenantId, dealId, loadFit, loadProfiles, loadDocs]);

  // Poll fit when panel is open and we don't have light/deep data yet (catches background jobs)
  useEffect(() => {
    if (!tenantId || !dealId || loading) return;
    const hasFit = fitData?.light || fitData?.deep;
    if (hasFit) return; // already have data, no need to poll
    const interval = setInterval(loadFit, 5000);
    return () => clearInterval(interval);
  }, [tenantId, dealId, loading, fitData?.light, fitData?.deep, loadFit]);

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length || !tenantId || !dealId) return;
    setUploading(true);
    try {
      const res = await uploadTenderDocs(tenantId, dealId, files);
      const ids = res.uploaded_file_ids || [];
      if (ids.length) {
        await loadDocs();
        toast.success(`Uploaded ${ids.length} file${ids.length > 1 ? 's' : ''}`);
      }
    } catch (err) {
      console.error('Upload failed:', err);
      toast.error(err.message || 'Failed to upload files');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleLightRefresh = async () => {
    if (!tenantId || !dealId) return;
    setLoading(true);
    try {
      console.log('[LiveRadar] starting light refresh', { dealId, tenantId });

      const refreshResult = await refreshLightFit(tenantId, dealId);

      console.log('[LiveRadar] light refresh queued', {
        dealId,
        tenantId,
        refreshResult,
      });

      const latest = await fetchFitLatest(tenantId, dealId);

      console.log('[LiveRadar] latest fit loaded after refresh', {
        dealId,
        tenantId,
        latest,
      });

      setFitData(latest);

      const fitDealId = deal?.fit_deal_id ?? dealId;
      if (typeof onFitLatestSynced === 'function' && latest && fitDealId) {
        console.log('[LiveRadar] latest returned for dealId (syncing list)', { fitDealId, latest });
        onFitLatestSynced(fitDealId, latest);
      }
    } catch (err) {
      console.error('[LiveRadar] light refresh failed', {
        dealId,
        tenantId,
        message: err?.message,
        error: err,
      });
      toast.error(err?.message || 'Light fit refresh failed');
    } finally {
      setLoading(false);
    }
  };

  const handleRunDeepFit = async () => {
    if (!tenantId || !dealId || !selectedProfileId || docs.length === 0) return;
    setDeepRunning(true);
    setDeepStatus('Queued...');
    try {
      const { job_id } = await triggerDeepFit(
        tenantId,
        dealId,
        selectedProfileId,
        docs.map((d) => d.id)
      );
      while (true) {
        const job = await pollFitJob(tenantId, job_id);
        setDeepStatus(job.status === 'processing' ? 'Processing...' : job.status === 'queued' ? 'Queued...' : job.status);
        if (job.status === 'complete') {
          await loadFit();
          break;
        }
        if (job.status === 'failed') throw new Error(job.error || 'Job failed');
        await new Promise((r) => setTimeout(r, 3000));
      }
    } catch (err) {
      console.error('Deep fit failed:', err);
      setDeepStatus(err.message || 'Failed');
      const msg = err.message || 'Deep fit failed';
      toast.error(msg.includes('not found') ? 'Deal not found for this tenant. The deal may need to be tracked or synced first.' : msg);
    } finally {
      setDeepRunning(false);
    }
  };

  const handleConfirmDeleteDoc = async () => {
    if (!docToDelete?.id || !tenantId || !dealId) return;
    const docId = docToDelete.id;
    setDeletingDocId(docId);
    try {
      const status = await deleteTenderDoc(tenantId, dealId, docId);
      if ((status >= 200 && status < 300) || status === 404) {
        await loadDocs();
        setDocToDelete(null);
      } else {
        toast.error(`Could not delete document (${status})`);
      }
    } catch (err) {
      console.error('Doc delete failed:', err);
      toast.error(err?.message || 'Could not delete document');
    } finally {
      setDeletingDocId(null);
    }
  };

  if (!dealId) return null;

  if (!tenantId) {
    return (
      <div className="space-y-4 pt-4 border-t">
        <h3 className="text-sm font-semibold text-[#0A1A2F]">{t('productFitPanel')}</h3>
        <p className="text-sm text-gray-500">Tenant not set</p>
      </div>
    );
  }

  return (
    <div className="space-y-4 pt-4 border-t">
      <h3 className="text-sm font-semibold text-[#0A1A2F]">{t('productFitPanel')}</h3>
      {loading ? (
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <Loader2 className="w-4 h-4 animate-spin" />
          Loading...
        </div>
      ) : (
        <>
          <div className="flex items-center justify-between gap-2">
            <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Light Fit</p>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 text-xs text-gray-500 hover:text-gray-700"
              onClick={handleLightRefresh}
            >
              <RefreshCw className="w-3.5 h-3.5 mr-1" />
              Refresh
            </Button>
          </div>
          <div>
            {fitData?.light ? (
              <>
                {fitData?.light_by_profile?.length > 1 && (
                  <p className="text-xs text-gray-500 mb-1">
                    Best fit: <span className="font-medium text-gray-700">{productLabel(fitData.light.profile_name)}</span>
                  </p>
                )}
                <FitSummaryBlock assessment={fitData.light} title="Light" />
                {fitData?.light_by_profile?.length > 1 && (
                  <div className="mt-3 space-y-1">
                    <p className="text-xs font-medium uppercase tracking-wide text-gray-500">By product</p>
                    {fitData.light_by_profile.map((p) => (
                      <div key={p.profile_id} className="flex items-center justify-between gap-2 text-sm">
                        <span className="text-gray-700">{productLabel(p.profile_name)}</span>
                        <span className="flex items-center gap-2 shrink-0">
                          <span className="font-medium">{p.score ?? '–'}</span>
                          <Badge variant="outline" className={`border text-xs ${FIT_LABEL_COLORS[p.label] || 'bg-gray-100 text-gray-600 border-gray-200'}`}>{FIT_LABEL_KEYS[p.label] ? t(FIT_LABEL_KEYS[p.label]) : p.label}</Badge>
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </>
            ) : (
              <p className="text-sm text-gray-500">No light fit assessment yet.</p>
            )}
          </div>

          <div className="pt-4 border-t mt-4">
            <p className="text-xs font-medium uppercase tracking-wide text-gray-500 mb-3">Deep Fit</p>
            <div className="space-y-3">
              <div>
                <Label className="text-xs">Product Profile</Label>
                <Select value={selectedProfileId} onValueChange={setSelectedProfileId}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select profile" />
                  </SelectTrigger>
                  <SelectContent>
                    {profiles.map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.name} {p.is_active && '(Active)'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Tender Documents</Label>
                <div className="mt-1 flex flex-wrap gap-2">
                  <label htmlFor="tender-docs-upload" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-dashed border-gray-300 hover:border-[#00C2C2] hover:bg-[#00C2C2]/5 cursor-pointer text-sm">
                    <Upload className="w-4 h-4" />
                    {uploading ? 'Uploading...' : 'Upload'}
                    <input
                      id="tender-docs-upload"
                      type="file"
                      multiple
                      accept=".pdf,.doc,.docx,.txt,.rtf,.odt"
                      className="hidden"
                      onChange={handleFileUpload}
                      disabled={uploading}
                    />
                  </label>
                </div>
                {docs.length > 0 && (
                  <ul className="mt-2 space-y-1">
                    {docs.map((d) => (
                      <li
                        key={d.id}
                        className="flex items-center justify-between gap-2 px-2 py-1.5 rounded bg-gray-50 text-xs"
                      >
                        <span className="inline-flex items-center gap-1.5 min-w-0">
                          <FileText className="w-3 h-3 shrink-0 text-gray-500" />
                          <span className="truncate">{d.file_name}</span>
                        </span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 shrink-0 text-gray-500 hover:text-red-600"
                          aria-label={`Delete ${d.file_name}`}
                          disabled={deletingDocId === d.id}
                          onClick={() => setDocToDelete(d)}
                        >
                          {deletingDocId === d.id ? (
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          ) : (
                            <Trash2 className="w-3.5 h-3.5" />
                          )}
                        </Button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <Button
                size="sm"
                onClick={handleRunDeepFit}
                disabled={!selectedProfileId || docs.length === 0 || deepRunning}
                className="bg-[#00C2C2] hover:bg-[#00A3A3] text-white"
              >
                {deepRunning ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {deepStatus}
                  </>
                ) : (
                  'Run Deep Fit'
                )}
              </Button>
              {fitData?.deep && (
                <div className="pt-2">
                  <p className="text-xs font-medium uppercase tracking-wide text-gray-500 mb-2">Deep Fit Results</p>
                  <FitSummaryBlock assessment={fitData.deep} title="Deep" />
                  {fitData.deep_requirements?.length > 0 && (
                    <div className="mt-3 overflow-x-auto">
                      <table className="w-full text-xs border-collapse">
                        <thead>
                          <tr className="border-b">
                            <th className="text-left py-2 font-medium">Requirement</th>
                            <th className="text-left py-2 font-medium">Type</th>
                            <th className="text-left py-2 font-medium">Category</th>
                            <th className="text-left py-2 font-medium">Match</th>
                          </tr>
                        </thead>
                        <tbody>
                          {fitData.deep_requirements.map((r) => (
                            <tr key={r.id} className="border-b border-gray-100">
                              <td className="py-2 max-w-[200px] truncate">{r.req_text}</td>
                              <td className="py-2">
                                <Badge variant="outline" className="text-[10px]">
                                  {REQ_TYPE_LABELS[r.req_type] || r.req_type}
                                </Badge>
                              </td>
                              <td className="py-2">{r.category}</td>
                              <td className="py-2">
                                <Badge className={MATCH_STATUS_COLORS[r.match_status] || ''}>
                                  {r.match_status}
                                </Badge>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {fitData?.light?.reasons?.length > 0 && (
            <Collapsible>
              <CollapsibleTrigger className="flex items-center gap-2 text-xs font-medium text-gray-600">
                <ChevronDown className="w-3 h-3" />
                Reasons
              </CollapsibleTrigger>
              <CollapsibleContent className="mt-2 pl-4 space-y-1 text-xs">
                {fitData.light.reasons.map((r, i) => (
                  <FitReasonRow key={i} reason={r} />
                ))}
              </CollapsibleContent>
            </Collapsible>
          )}
          {fitData?.light?.gates?.length > 0 && (
            <Collapsible>
              <CollapsibleTrigger className="flex items-center gap-2 text-xs font-medium text-gray-600">
                <ChevronDown className="w-3 h-3" />
                Gates
              </CollapsibleTrigger>
              <CollapsibleContent className="mt-2 pl-4 space-y-1 text-xs">
                {fitData.light.gates.map((g, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <span className={g.result === 'pass' ? 'text-green-600' : g.result === 'fail' ? 'text-red-600' : 'text-gray-500'}>
                      {g.result}
                    </span>
                    <span>{g.name}</span>
                  </div>
                ))}
              </CollapsibleContent>
            </Collapsible>
          )}
          {fitData?.light?.unknowns?.length > 0 && (
            <Collapsible>
              <CollapsibleTrigger className="flex items-center gap-2 text-xs font-medium text-gray-600">
                <ChevronDown className="w-3 h-3" />
                Unknowns
              </CollapsibleTrigger>
              <CollapsibleContent className="mt-2 pl-4 space-y-1 text-xs">
                {fitData.light.unknowns.map((u, i) => (
                  <div key={i}>
                    <span className="font-medium">{u.topic}:</span> {u.why}
                  </div>
                ))}
              </CollapsibleContent>
            </Collapsible>
          )}
        </>
      )}
      <AlertDialog open={docToDelete != null} onOpenChange={(open) => !open && !deletingDocId && setDocToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete tender document?</AlertDialogTitle>
            <AlertDialogDescription>
              {docToDelete?.file_name
                ? `"${docToDelete.file_name}" will be permanently removed from this deal. This cannot be undone.`
                : 'This document will be permanently removed from this deal. This cannot be undone.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deletingDocId != null}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDeleteDoc}
              disabled={deletingDocId != null}
              className="bg-red-600 hover:bg-red-700"
            >
              {deletingDocId != null ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                'Delete'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
